import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Crown, Gavel, User, CreditCard, Shield, Loader2, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const Dashboard = ({ user, breederProfile }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(true);
  const [settingUp, setSettingUp] = useState(false);

  const isBreeder = user?.roles?.includes('breeder');
  const requestedReturn = searchParams.get("return_to");
  const returnTo = requestedReturn?.startsWith("/") && !requestedReturn.startsWith("//")
    ? requestedReturn
    : "/dashboard";

  useEffect(() => {
    if (!user) return;
    fetchPaymentStatus();
    
    // Handle Stripe setup callback
    const setupComplete = searchParams.get("setup_complete");
    const sessionId = searchParams.get("session_id");
    if (setupComplete === "true" && sessionId) {
      confirmSetup(sessionId);
    }
    const setupCancelled = searchParams.get("setup_cancelled");
    if (setupCancelled === "true") {
      toast.error("Payment method setup was cancelled.");
      searchParams.delete("setup_cancelled");
      setSearchParams(searchParams, { replace: true });
    }
  }, [user]);

  const fetchPaymentStatus = async () => {
    try {
      const res = await api.get("/bidder/payment-status");
      setPaymentStatus(res.data);
    } catch (err) { logger.error("Fetch error:", err); } finally {
      setLoadingPayment(false);
    }
  };

  const confirmSetup = async (sessionId) => {
    let confirmed = false;
    try {
      const res = await api.post("/bidder/confirm-payment-method", { session_id: sessionId });
      toast.success(res.data.message);
      await fetchPaymentStatus();
      confirmed = true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to verify payment method");
    } finally {
      searchParams.delete("setup_complete");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
      if (confirmed && returnTo !== "/dashboard") {
        navigate(returnTo, { replace: true });
      }
    }
  };

  const handleSetupPayment = async () => {
    setSettingUp(true);
    try {
      const res = await api.post("/bidder/setup-intent", {
        origin_url: window.location.origin,
        return_to: returnTo,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start payment setup");
      setSettingUp(false);
    }
  };

  const hasPayment = paymentStatus?.has_payment_method;
  const cardInfo = paymentStatus?.card_info;

  if (!user) return null;

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Dashboard</p>
            <h1 className="text-4xl text-[#EDEDED]">Welcome, {user.name?.split(' ')[0]}</h1>
          </div>
          {user.picture && (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-full border-2 border-[#D4AF37]/30" />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-6 h-6 text-[#D4AF37]" />
              <h3 className="text-xl text-[#EDEDED]">Buyer Account</h3>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-4">Browse listings, save favorites, and connect with breeders.</p>
            <Link to="/" className="btn-secondary text-sm inline-flex items-center gap-2">
              Browse Listings <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-6 h-6 text-[#D4AF37]" />
              <h3 className="text-xl text-[#EDEDED]">Breeder Account</h3>
            </div>
            {isBreeder ? (
              <>
                <p className="text-[#A1A1AA] text-sm mb-4">Manage your listings and breeder profile.</p>
                <Link to="/dashboard/breeder" className="btn-gold text-sm inline-flex items-center gap-2">
                  Breeder Dashboard <ChevronRight className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <>
                <p className="text-[#A1A1AA] text-sm mb-4">Start selling on Breedling&apos;s premium marketplace.</p>
                <Link to="/subscription-plans" className="btn-gold text-sm inline-flex items-center gap-2">
                  Start Listing <ChevronRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Payment & Billing is an account-level concern, separate from auctions. */}
          <div
            className={`glass-card p-6 ${hasPayment ? "border-[#22C55E]/30" : "border-[#D4AF37]/30"}`}
            data-testid="payment-billing-card"
          >
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className={`w-6 h-6 ${hasPayment ? "text-[#22C55E]" : "text-[#D4AF37]"}`} />
              <h3 className="text-xl text-[#EDEDED]">Payment & Billing</h3>
              {hasPayment && (
                <span className="px-2 py-0.5 bg-[#22C55E]/10 text-[#22C55E] text-xs rounded-full flex items-center gap-1">
                  <Check className="w-3 h-3" /> Card saved
                </span>
              )}
            </div>

            {loadingPayment ? (
              <div className="flex items-center gap-2 text-[#71717A]">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking payment details...
              </div>
            ) : (
              <>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  Manage the payment method attached to your Breedling account. Subscription plans are selected separately from the Plans page.
                </p>
                {hasPayment && cardInfo && (
                  <div className="flex items-center gap-3 p-3 bg-[#18181B] rounded-lg mb-4">
                    <Shield className="w-5 h-5 text-[#22C55E]" />
                    <div>
                      <p className="text-sm text-[#EDEDED] capitalize">{cardInfo.brand} ****{cardInfo.last4}</p>
                      <p className="text-xs text-[#71717A]">Expires {cardInfo.exp_month}/{cardInfo.exp_year}</p>
                    </div>
                  </div>
                )}
                <button
                  onClick={handleSetupPayment}
                  disabled={settingUp}
                  className={`${hasPayment ? "btn-secondary" : "btn-gold"} text-sm inline-flex items-center gap-2 disabled:opacity-50`}
                  data-testid="setup-payment-btn"
                >
                  {settingUp ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Opening Stripe...</>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      {hasPayment ? "Update Payment Method" : "Add Payment Method"}
                      <ExternalLink className="w-3 h-3" />
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          {/* Auction participation and bidder status stay together. */}
          <div className="glass-card p-6" data-testid="auctions-card">
            <div className="flex items-center gap-3 mb-4">
              <Gavel className="w-6 h-6 text-[#D4AF37]" />
              <h3 className="text-xl text-[#EDEDED]">Auctions</h3>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-4">
              Track your bids and manage your auction listings.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs uppercase tracking-wider text-[#71717A]">Bidder status</span>
              {hasPayment ? (
                <span className="px-2 py-0.5 bg-[#22C55E]/10 text-[#22C55E] text-xs rounded-full flex items-center gap-1">
                  <Check className="w-3 h-3" /> Verified
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded-full">
                  Payment method required
                </span>
              )}
            </div>
            {!loadingPayment && !hasPayment && (
              <p className="text-[#71717A] text-xs mb-4">
                Auction verification is only required to bid. Add a card under Payment & Billing; bidding is free and the card is charged only if you win.
              </p>
            )}
            <Link to="/my/auctions" className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="my-auctions-link">
              My Auctions <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="glass-card p-6">
          <h3 className="text-xl text-[#EDEDED] mb-4">Account Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-[#A1A1AA]">Email</p>
              <p className="text-[#EDEDED]">{user.email}</p>
            </div>
            <div>
              <p className="text-[#A1A1AA]">Roles</p>
              <p className="text-[#EDEDED] capitalize">{user.roles?.join(', ')}</p>
            </div>
            <div>
              <p className="text-[#A1A1AA]">Member Since</p>
              <p className="text-[#EDEDED]">{new Date(user.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-[#A1A1AA]">Status</p>
              <p className="text-green-400">Active</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;


